import { useImperativeHandle, useRef, useState } from "react";
import { AtSign, Paperclip, X } from "lucide-react";
import { UserAvatar, type UserAvatarUser } from "@/components/ui/user-avatar";
import { Button } from "@/components/ui/button";
import {
  COMPOSER_ACTION_CLASS,
  ComposerSendButton,
  ComposerShell,
} from "@/components/chat/thread/ComposerShell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { AttachMenuItems } from "@/domains/pm/components/issues/AttachMenuItems";
import {
  MentionTextarea,
  type MentionInsertion,
  type MentionTextareaHandle,
  type MentionUser,
} from "@/domains/pm/components/issues/MentionTextarea";
import {
  IssueAttachmentsStager,
  type IssueAttachmentsStagerHandle,
  type StagedAttachment,
} from "@/domains/pm/components/issues/IssueAttachmentsStager";
import { CHAT_ROW_AVATAR_SIZE } from "./ChatThreadRow";

/** Strings the composer renders. Supplied by the caller so the shared
 *  composer stays free of any one surface's i18n namespace. */
export interface ChatComposerLabels {
  placeholder: string;
  attach: string;
  /** Only needed when mentions are on. */
  mention?: string;
  send: string;
  removeQuote: string;
}

export interface ChatComposerHandle {
  /**
   * Start a reply: append `@Name ` to the draft, register the mention (when the
   * author is a resolvable project member) and hand over the caret.
   */
  appendMention: (mention: { userId?: number; displayName: string }) => void;
  focusEnd: () => void;
  /** Stage existing documents in the draft (skipping any already staged). */
  stageDocuments: (documents: StagedAttachment[]) => void;
}

/** Documents offered as one-click attachments at the top of the attach menu. */
export interface ComposerAttachShortcuts {
  /** The group's heading in the menu, e.g. "In the brief". */
  label: string;
  documents: StagedAttachment[];
}

/** Adds `extra` to `current`, keeping order and dropping documents already there. */
function withStaged(current: StagedAttachment[], extra: StagedAttachment[]) {
  const seen = new Set(current.map((d) => d.id));
  return [...current, ...extra.filter((d) => !seen.has(d.id) && seen.add(d.id))];
}

export interface ChatComposerProps {
  buildingId: number;
  /** Project the draft belongs to — scopes mention suggestions and uploads. */
  projectId: number;
  /** The viewing user — their avatar leads the pill. */
  currentUser?: UserAvatarUser | null;
  labels: ChatComposerLabels;
  /**
   * Read-only quote of the message being replied to, rendered above the pill.
   * Held by the caller (it also owns whatever the quote means on send — a
   * quoted body for the issue thread, a reply id for the message board).
   */
  quote?: string | null;
  onRemoveQuote?: () => void;
  /**
   * Post the draft. The composer clears straight away and, when this resolves
   * `false`, puts the text, mentions and staged documents back — a failed post
   * must never cost the user what they wrote.
   */
  onSend: (
    body: string,
    mentionedUserIds: number[],
    documentIds: number[]
  ) => void | boolean | Promise<boolean>;
  /** Storage prefix for uploaded attachments — see IssueAttachmentsStager. */
  storagePathSegments?: string[];
  /** Project folder uploaded attachments are filed into — see IssueAttachmentsStager. */
  targetFolderName?: string;
  /**
   * False drops the "@" button and makes `@` plain text — for a surface with no
   * mention notifications behind it. Defaults to true.
   */
  mentions?: boolean;
  className?: string;
  handleRef?: React.Ref<ChatComposerHandle>;
  /** An explicit mention audience instead of the project's members. */
  mentionUsers?: MentionUser[];
  /**
   * False drops the attach menu and the staging strip — document comment
   * threads carry no attachments, and `onSend` then always receives `[]`.
   */
  attachments?: boolean;
  /** Known documents to attach in one click, listed above the attach menu's other items. */
  attachShortcuts?: ComposerAttachShortcuts;
  /** The draft to start from — editing something already posted. */
  initialBody?: string;
  initialAttachments?: StagedAttachment[];
}

/**
 * Read-only quote of the message being replied to — same grey left-bar
 * treatment as a sent quote, but structurally NOT editable: only the textarea
 * below takes input, so a reply can never rewrite the words it quotes
 * (WHA-3810 round 3).
 */
function ComposerQuote({
  quote,
  label,
  onRemove,
}: {
  quote: string;
  label: string;
  onRemove?: () => void;
}) {
  return (
    <div className="mb-1 flex items-start gap-2">
      <blockquote className="min-w-0 flex-1 whitespace-pre-wrap border-l-2 border-muted-foreground/30 pl-2 text-xs text-muted-foreground">
        {quote}
      </blockquote>
      <button
        type="button"
        onClick={onRemove}
        aria-label={label}
        title={label}
        className="flex-shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/** Attach menu, mention picker and send — the pill's trailing icon run. */
function ComposerActions(props: {
  labels: ChatComposerLabels;
  canSend: boolean;
  showAttach: boolean;
  shortcuts?: ComposerAttachShortcuts;
  staged: StagedAttachment[];
  onStage: (document: StagedAttachment) => void;
  onAddFromFranklin: () => void;
  onUpload: () => void;
  /** Omitted when mentions are off — the "@" button is not rendered. */
  onMention?: () => void;
  onSend: () => void;
}) {
  const { labels, canSend } = props;
  return (
    <>
      {props.showAttach && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={COMPOSER_ACTION_CLASS}
              aria-label={labels.attach}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {props.shortcuts && props.shortcuts.documents.length > 0 && (
              <>
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  {props.shortcuts.label}
                </DropdownMenuLabel>
                <div className="max-h-56 overflow-y-auto">
                  {props.shortcuts.documents.map((doc) => (
                    <DropdownMenuItem
                      key={doc.id}
                      disabled={props.staged.some((d) => d.id === doc.id)}
                      onSelect={() => props.onStage(doc)}
                    >
                      <FileTypeIcon filename={doc.filename} className="mr-2 size-4 shrink-0" />
                      <span className="truncate">{doc.filename}</span>
                    </DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <AttachMenuItems
              onAddFromFranklin={props.onAddFromFranklin}
              onUpload={props.onUpload}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {props.onMention && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={props.onMention}
          aria-label={labels.mention}
          className={COMPOSER_ACTION_CLASS}
        >
          <AtSign className="h-4 w-4" />
        </Button>
      )}
      <ComposerSendButton canSend={canSend} onClick={props.onSend} label={labels.send} />
    </>
  );
}

/**
 * The in-progress message: its text, the mentions inserted into it and the
 * documents staged alongside it — plus the send that flushes all three.
 */
function useChatDraft(
  props: ChatComposerProps,
  textareaRef: React.RefObject<MentionTextareaHandle>
) {
  const [body, setBody] = useState(props.initialBody ?? "");
  // Track each mention's display name alongside its user id so we can drop
  // mentions whose `@DisplayName` text the user deleted before submitting
  // (WHA-3300). Duplicates are allowed; dedup happens on submit.
  const [mentions, setMentions] = useState<MentionInsertion[]>([]);
  const [attachments, setAttachments] = useState<StagedAttachment[]>(
    props.initialAttachments ?? []
  );
  const canSend = body.trim().length > 0 || attachments.length > 0;

  useImperativeHandle(props.handleRef, () => ({
    appendMention: ({ userId, displayName }) => {
      setBody((prev) => (prev.trim() ? `${prev.trimEnd()}\n@${displayName} ` : `@${displayName} `));
      if (userId != null) setMentions((prev) => [...prev, { userId, displayName }]);
      textareaRef.current?.focusEnd();
    },
    focusEnd: () => textareaRef.current?.focusEnd(),
    stageDocuments: (documents) => setAttachments((prev) => withStaged(prev, documents)),
  }));

  const handleSend = () => {
    if (!canSend) return;
    const trimmed = body.trim();
    const sent = { mentions, attachments };
    // Keep only mentions whose `@DisplayName` still appears in the body.
    // De-mentioned users (their @-chunk removed from the textarea) drop out
    // and so do not receive an email notification.
    const activeIds = new Set(
      mentions.filter((m) => trimmed.includes(`@${m.displayName}`)).map((m) => m.userId)
    );
    const accepted = props.onSend(
      trimmed,
      Array.from(activeIds),
      attachments.map((d) => d.id)
    );
    setBody("");
    setMentions([]);
    setAttachments([]);
    void Promise.resolve(accepted).then((result) => {
      if (result !== false) return;
      // Hand the draft back, ahead of anything typed since it was sent.
      setBody((current) =>
        current.trim() === trimmed ? current : [trimmed, current].filter(Boolean).join("\n\n")
      );
      setMentions((current) => [...sent.mentions, ...current]);
      setAttachments((current) => [
        ...sent.attachments.filter((staged) => !current.some((d) => d.id === staged.id)),
        ...current,
      ]);
    });
  };

  const addMention = (mention: MentionInsertion) => setMentions((prev) => [...prev, mention]);

  return { body, setBody, mentions, addMention, attachments, setAttachments, canSend, handleSend };
}

/** Everything on one midline: avatar, input and the action icons are
 *  centre-aligned in the pill (WHA-3810 round 3). */
function ComposerPill(props: {
  currentUser?: UserAvatarUser | null;
  labels: ChatComposerLabels;
  projectId: number;
  mentions: boolean;
  mentionUsers?: MentionUser[];
  showAttach: boolean;
  shortcuts?: ComposerAttachShortcuts;
  draft: ReturnType<typeof useChatDraft>;
  stagerRef: React.RefObject<IssueAttachmentsStagerHandle>;
  textareaRef: React.RefObject<MentionTextareaHandle>;
}) {
  const { labels, draft, stagerRef, textareaRef } = props;
  return (
    <ComposerShell
      leading={
        <UserAvatar
          user={props.currentUser}
          size={CHAT_ROW_AVATAR_SIZE}
          className="flex-shrink-0"
        />
      }
      actions={
        <ComposerActions
          labels={labels}
          canSend={draft.canSend}
          showAttach={props.showAttach}
          shortcuts={props.shortcuts}
          staged={draft.attachments}
          onStage={(doc) => draft.setAttachments((prev) => withStaged(prev, [doc]))}
          onAddFromFranklin={() => stagerRef.current?.openPicker()}
          onUpload={() => stagerRef.current?.openUpload()}
          onMention={props.mentions ? () => textareaRef.current?.openMentionPicker() : undefined}
          onSend={draft.handleSend}
        />
      }
    >
      <MentionTextarea
        value={draft.body}
        onChange={draft.setBody}
        handleRef={textareaRef}
        onMentionAdd={draft.addMention}
        onSubmit={draft.handleSend}
        placeholder={labels.placeholder}
        rows={1}
        submitOnEnter
        mentions={props.mentions}
        projectId={props.projectId}
        users={props.mentionUsers}
        className="rounded-none border-transparent px-0 py-1.5 focus:ring-0"
      />
    </ComposerShell>
  );
}

/**
 * The chat composer pill — avatar, mention-aware input, attachment staging,
 * mention picker and send.
 *
 * Shared by the PM issue-panel comment thread and the building Dashboard's
 * message board — one implementation, so both surfaces stay identical.
 */
export function ChatComposer(props: ChatComposerProps) {
  const {
    buildingId,
    projectId,
    labels,
    quote = null,
    mentions = true,
    attachments = true,
  } = props;
  const stagerRef = useRef<IssueAttachmentsStagerHandle>(null);
  const textareaRef = useRef<MentionTextareaHandle>(null);
  const draft = useChatDraft(props, textareaRef);

  return (
    <div className={props.className}>
      {attachments && (
        <IssueAttachmentsStager
          ref={stagerRef}
          value={draft.attachments}
          onChange={draft.setAttachments}
          buildingId={String(buildingId)}
          projectId={String(projectId)}
          storagePathSegments={props.storagePathSegments}
          targetFolderName={props.targetFolderName}
          hideLabel
          hideActions
          className="pb-1"
        />
      )}
      {quote !== null && (
        <ComposerQuote quote={quote} label={labels.removeQuote} onRemove={props.onRemoveQuote} />
      )}
      <ComposerPill
        currentUser={props.currentUser}
        labels={labels}
        projectId={projectId}
        mentions={mentions}
        mentionUsers={props.mentionUsers}
        showAttach={attachments}
        shortcuts={props.attachShortcuts}
        draft={draft}
        stagerRef={stagerRef}
        textareaRef={textareaRef}
      />
    </div>
  );
}
